import { useCallback, useEffect, useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import { useToast } from '../../../components/toast/ToastProvider';
import {
  generateEstimateAnalysisEndpoint,
  getEstimateEndpoint,
  recomputeEstimateEndpoint,
  saveEstimateAnalysisEndpoint,
} from '../services/propertyService';
import {
  EstimateMarket,
  PropertyEstimateData,
  PropertyEstimates,
} from '../models/estimateModel';

// Evento para que quien guarda un precio avise a la tarjeta (el recálculo corre en segundo
// plano en rs-service y tarda ~1-2 s).
export const ESTIMATE_REFRESH_EVENT = 'property-estimate-refresh';
const BACKGROUND_RECOMPUTE_WAIT_MS = 3000;
// El análisis con IA se genera después del cálculo (~15 s): un pedido más para recogerlo.
const BACKGROUND_ANALYSIS_WAIT_MS = 20000;

const isOk = (status: number) => status === 200 || status === 201;
const unwrap = (data: any) => data?.data ?? data;

interface Props {
  propertyId: string;
  market: EstimateMarket;
}

const usePropertyEstimate = ({ propertyId, market }: Props) => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const toast = useToast();
  const [estimate, setEstimate] = useState<PropertyEstimateData | null>(null);
  const [loaded, setLoaded] = useState(false);

  const apply = (data: any) => {
    const estimates: PropertyEstimates = unwrap(data);
    setEstimate(estimates?.[market] ?? null);
  };

  const refresh = useCallback(async () => {
    const { status, data } = await callEndpoint(getEstimateEndpoint(propertyId, token));
    if (isOk(status)) apply(data);
    setLoaded(true);
  }, [propertyId, market]);

  useEffect(() => {
    refresh().catch((e) => console.error(e));
    // Segundo pedido: si se acaba de guardar un precio, el recálculo en segundo plano ya terminó.
    const timers: number[] = [];
    const later = (delay: number) =>
      timers.push(window.setTimeout(() => refresh().catch(() => undefined), delay));
    later(BACKGROUND_RECOMPUTE_WAIT_MS);
    later(BACKGROUND_ANALYSIS_WAIT_MS);
    const onRefresh = () => {
      later(BACKGROUND_RECOMPUTE_WAIT_MS);
      later(BACKGROUND_ANALYSIS_WAIT_MS);
    };
    window.addEventListener(ESTIMATE_REFRESH_EVENT, onRefresh);
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener(ESTIMATE_REFRESH_EVENT, onRefresh);
    };
  }, [refresh]);

  const recompute = async () => {
    const { status, data } = await callEndpoint(
      recomputeEstimateEndpoint(propertyId, market, token),
    );
    if (!isOk(status)) {
      toast.error(unwrap(data)?.message ?? 'No se pudo recalcular el estimate.');
      return;
    }
    apply(data);
    toast.success('Estimate recalculado.');
  };

  const saveAnalysis = async (text: string) => {
    const { status, data } = await callEndpoint(
      saveEstimateAnalysisEndpoint(propertyId, market, text, token),
    );
    if (!isOk(status)) {
      toast.error(unwrap(data)?.message ?? 'No se pudo guardar el análisis.');
      return;
    }
    apply(data);
    toast.success('Análisis guardado.');
  };

  const generateAnalysis = async () => {
    const { status, data } = await callEndpoint(
      generateEstimateAnalysisEndpoint(propertyId, market, token),
    );
    if (!isOk(status)) {
      toast.error(unwrap(data)?.message ?? 'No se pudo generar el análisis.');
      return;
    }
    apply(data);
    toast.success('Análisis generado.');
  };

  return { estimate, loaded, isLoading, refresh, recompute, saveAnalysis, generateAnalysis };
};

export default usePropertyEstimate;
